import { CreditCardIcon, EditIcon } from 'lucide-react';

import React from 'react';

import { formatDate } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { ExpenseResponseDto } from '@splitfiesta/shared';
import { Avatar, Button, Card } from '@splitfiesta/ui';

import { UserPositionIndicator } from '@/components/activity/UserPositionIndicator';
import { LockedAction } from '@/components/common/LockedAction';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { SupporterBadge } from '@/components/pro/SupporterBadge';
import { SocialInteractions } from '@/components/social/SocialInteractions';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { RelativeTime } from '@/components/ui/RelativeTime';

import { resolveUserName } from '@/lib/displayName';

import { useAuthStore } from '@/stores/authStore';

import { ExpenseCategoryIcon } from '@/utils/expenseCategoryIcons';

// No-op functions to avoid empty arrow function lint warnings
const noop = () => undefined;
const asyncNoop = async () => undefined;

interface ExpenseCardProps {
  expense: Pick<
    ExpenseResponseDto,
    | 'id'
    | 'groupId'
    | 'title'
    | 'description'
    | 'notes'
    | 'amount'
    | 'currency'
    | 'category'
    | 'subcategory'
    | 'date'
    | 'createdAt'
    | 'emoji'
    | 'payers'
    | 'splits'
    | 'reactions'
    | 'aggregatedReactions'
    | 'comments'
    | 'totalComments'
    | 'originalAmount'
    | 'originalCurrency'
  > & {
    hasMoreComments?: boolean;
  };
  groupName?: string;
  // Handlers
  onAddReaction?: (emoji: string) => void;
  onToggleReaction?: (emoji: string, hasReacted: boolean) => void;
  onSubmitComment?: (text: string) => Promise<void>;
  onEditComment?: (commentId: string, text: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  onLoadMoreComments?: () => void | Promise<void>;
  loadingReaction?: boolean;
  loadingMoreComments?: boolean;
  // Layout options
  variant?: 'full' | 'compact' | 'feed';
  showNotes?: boolean;
  showMultiplePayers?: boolean;
  showPositionIndicator?: boolean;
  showSocialInteractions?: boolean;
  isClickable?: boolean;
  // Edit button
  editHref?: string;
  /**
   * Shown in place of the edit button when the viewer may not edit —
   * the reason, rendered as a locked control (see `LockedAction`).
   */
  editLockedMessage?: string;
  /** This surface shows Supporter badges; the badge checks the `pro` flag. */
  showSupporterBadges?: boolean;
}

function ExpenseCardComponent({
  expense,
  groupName,
  onAddReaction,
  onToggleReaction,
  onSubmitComment,
  onEditComment,
  onDeleteComment,
  onLoadMoreComments,
  loadingReaction = false,
  loadingMoreComments = false,
  variant = 'full',
  showNotes = true,
  showMultiplePayers = true,
  showPositionIndicator = true,
  showSocialInteractions = true,
  isClickable = false,
  editHref,
  editLockedMessage,
  showSupporterBadges = false,
}: ExpenseCardProps) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore(state => state.user);

  // Load required translation namespaces
  useNamespace('expenses');
  useNamespace('common');
  useNamespace('groups');
  useNamespace('social');

  const mainPayer = expense.payers[0];
  const hasMultiplePayers = expense.payers.length > 1;
  const original =
    expense.originalAmount != null && expense.originalCurrency != null
      ? { amount: expense.originalAmount, currency: expense.originalCurrency }
      : undefined;

  // Feed variant - compact two-column layout
  if (variant === 'feed') {
    const feedContent = (
      <>
        {/* Compact Expense Info - Two Column Layout */}
        <div className="flex gap-3 pointer-events-none">
          {/* Icon - 32px */}
          <ExpenseCategoryIcon
            category={expense.category}
            subcategory={expense.subcategory}
            className="w-8 h-8 text-orange-500 dark:text-orange-400 shrink-0"
          />

          {/* Left column: Title + Group + People */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base line-clamp-1 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
              {expense.emoji && <span className="mr-1">{expense.emoji}</span>}
              {expense.title || expense.description}
            </h4>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {groupName && (
                <RouteChangeLink
                  href={`/groups/${expense.groupId}`}
                  className="text-primary-600 dark:text-primary-400 hover:underline pointer-events-auto"
                  onClick={e => e.stopPropagation()}
                >
                  {groupName}
                </RouteChangeLink>
              )}
              {expense.splits.length > 0 && (
                <div className="mt-0.5">
                  {expense.splits.length} {t('common:labels.people')}
                </div>
              )}
            </div>
          </div>

          {/* Right column: Amount + Time + Position */}
          <div className="text-right shrink-0">
            <div className="font-bold text-base sm:text-lg text-gray-900 dark:text-white group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors">
              <CurrencyDisplay
                amount={expense.amount}
                currency={expense.currency}
                original={original}
              />
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              <RelativeTime date={expense.createdAt} />
            </div>
            {/* Position Indicator - compact label + signed amount */}
            {showPositionIndicator && user?.id && (
              <div className="mt-1 flex justify-end">
                <UserPositionIndicator
                  userId={user.id}
                  paidBy={expense.payers.map(p => ({
                    userId: p.userId,
                    amount: p.amount,
                  }))}
                  splits={expense.splits.map(s => ({
                    userId: s.userId,
                    amount: s.amount,
                  }))}
                  currency={expense.currency}
                  variant="compact"
                />
              </div>
            )}
          </div>
        </div>

        {/* Social Interactions */}
        {showSocialInteractions && (
          <div className="mt-3 pointer-events-auto">
            <SocialInteractions
              reactions={expense.aggregatedReactions || []}
              onAddReaction={onAddReaction || noop}
              onToggleReaction={onToggleReaction || noop}
              loadingReaction={loadingReaction}
              comments={expense.comments || []}
              currentUserId={user?.id}
              currentUserName={user?.name || user?.email || 'Unknown'}
              currentUserProfileImage={user?.profileImage ?? undefined}
              onSubmitComment={onSubmitComment || asyncNoop}
              onEditComment={onEditComment || asyncNoop}
              onDeleteComment={onDeleteComment || asyncNoop}
              hasMoreComments={expense.hasMoreComments}
              onLoadMoreComments={onLoadMoreComments}
              loadingMoreComments={loadingMoreComments}
              variant="compact"
              showDivider={false}
              showSupporterBadges={showSupporterBadges}
            />
          </div>
        )}
      </>
    );

    if (isClickable) {
      return (
        <Card
          className="p-4 hover:shadow-md transition-shadow group relative"
          data-testid={`expense-card-${expense.id}`}
        >
          <RouteChangeLink
            href={`/groups/${expense.groupId}/expenses/${expense.id}`}
            className="absolute inset-0 z-0"
            aria-label={`View expense: ${expense.title}`}
          />
          <div className="relative z-10 pointer-events-none [&_.pointer-events-auto]:pointer-events-auto">
            {feedContent}
          </div>
        </Card>
      );
    }

    return (
      <Card className="p-4" data-testid={`expense-card-${expense.id}`}>
        {feedContent}
      </Card>
    );
  }

  const cardContent = (
    <>
      {/* Header section */}
      <div className="flex items-start gap-3 mb-1">
        <ExpenseCategoryIcon
          category={expense.category}
          subcategory={expense.subcategory}
          className="w-8 h-8 text-orange-500 dark:text-orange-400 shrink-0"
        />
        <div className="flex-1 min-w-0">
          {/* First line: Amount */}
          <span className="text-lg font-bold text-gray-900 dark:text-white transition-colors duration-200 group-hover:text-orange-500 dark:group-hover:text-orange-400">
            <CurrencyDisplay
              amount={expense.amount}
              currency={expense.currency}
              original={original}
            />
          </span>

          {/* Second line: Expense title */}
          <div className="mt-0.5 font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-200 group-hover:text-orange-500 dark:group-hover:text-orange-400">
            {expense.emoji && <span className="mr-1">{expense.emoji}</span>}
            {expense.title || expense.description}
          </div>

          {/* Third line: Who paid */}
          <div
            className="mt-0.5 flex min-w-0 items-center text-sm text-gray-500 dark:text-gray-400"
            title={resolveUserName(mainPayer?.userName, t)}
          >
            <span className="min-w-0 truncate">
              {resolveUserName(mainPayer?.userName, t)}
            </span>
            {showSupporterBadges && mainPayer?.isSupporter && (
              <SupporterBadge className="ml-1 shrink-0" />
            )}
          </div>

          {/* Fourth line: Category and date */}
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <span>
                {expense.category
                  ? t(`expenses:categories.${expense.category}`)
                  : t('expenses:categories.other')}
              </span>
              {expense.subcategory && expense.subcategory !== 'default' && (
                <>
                  <span>•</span>
                  <span>
                    {expense.category &&
                    t(
                      `expenses:subcategories.${expense.category}.${expense.subcategory}`
                    ) !==
                      `expenses:subcategories.${expense.category}.${expense.subcategory}`
                      ? t(
                          `expenses:subcategories.${expense.category}.${expense.subcategory}`
                        )
                      : expense.subcategory.charAt(0).toUpperCase() +
                        expense.subcategory.slice(1)}
                  </span>
                </>
              )}
            </div>
            <div>{formatDate(expense.date, i18n.language)}</div>
          </div>
        </div>

        {/* Right column: Edit button + Timestamp + Position indicator */}
        <div className="text-right shrink-0">
          {/* Edit button */}
          {editHref ? (
            <div className="mb-2">
              <RouteChangeLink href={editHref}>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<EditIcon className="w-4 h-4" />}
                >
                  {t('common:actions.edit')}
                </Button>
              </RouteChangeLink>
            </div>
          ) : editLockedMessage ? (
            <div className="mb-2">
              <LockedAction
                message={editLockedMessage}
                testId="expense-card-edit-locked"
              >
                <EditIcon className="w-4 h-4 mr-2" />
                {t('common:actions.edit')}
              </LockedAction>
            </div>
          ) : null}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <RelativeTime date={expense.createdAt} />
          </div>
          {/* Position Indicator - compact label + signed amount */}
          {showPositionIndicator && user?.id && (
            <div className="mt-1 flex justify-end">
              <UserPositionIndicator
                userId={user.id}
                paidBy={expense.payers.map(p => ({
                  userId: p.userId,
                  amount: p.amount,
                }))}
                splits={expense.splits.map(s => ({
                  userId: s.userId,
                  amount: s.amount,
                }))}
                currency={expense.currency}
                variant="compact"
              />
            </div>
          )}
        </div>
      </div>

      {/* Notes - only in full variant */}
      {variant === 'full' && showNotes && expense.notes && (
        <div className="mb-4 p-4 bg-[#F6F5F3] dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {expense.notes}
          </p>
        </div>
      )}

      {/* Multiple payers section - only show if more than one payer and in full variant */}
      {variant === 'full' && showMultiplePayers && hasMultiplePayers && (
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-3 text-xs text-gray-500 dark:text-gray-400">
            <CreditCardIcon className="w-3.5 h-3.5" />
            <span className="uppercase tracking-wider">
              {t('expenses:fields.paidBy')}
            </span>
          </div>
          <div className="space-y-2">
            {expense.payers.map((payer, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2"
              >
                <div className="flex items-center gap-3">
                  <Avatar
                    name={payer.userName || 'Unknown'}
                    src={payer.profileImage}
                    size="sm"
                  />
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {resolveUserName(payer.userName, t)}
                      {showSupporterBadges && payer.isSupporter && (
                        <SupporterBadge className="ml-1" />
                      )}
                    </span>
                    {payer.userId === user?.id && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {t('expenses:labels.you')}
                      </span>
                    )}
                  </div>
                </div>
                <CurrencyDisplay
                  amount={payer.amount}
                  currency={expense.currency}
                  className="font-semibold text-gray-900 dark:text-gray-100"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Social Section - Reactions and Comments */}
      {showSocialInteractions && (
        <div className="mt-3 pointer-events-auto">
          <SocialInteractions
            reactions={expense.aggregatedReactions || []}
            onAddReaction={
              onAddReaction ||
              (() => {
                // No-op when handler not provided
              })
            }
            onToggleReaction={
              onToggleReaction ||
              (() => {
                // No-op when handler not provided
              })
            }
            loadingReaction={loadingReaction}
            comments={expense.comments || []}
            currentUserId={user?.id}
            currentUserName={user?.name || user?.email || 'Unknown'}
            currentUserProfileImage={user?.profileImage ?? undefined}
            onSubmitComment={
              onSubmitComment ||
              (async () => {
                // No-op when handler not provided
              })
            }
            onEditComment={
              onEditComment ||
              (async () => {
                // No-op when handler not provided
              })
            }
            onDeleteComment={
              onDeleteComment ||
              (async () => {
                // No-op when handler not provided
              })
            }
            hasMoreComments={expense.hasMoreComments}
            onLoadMoreComments={onLoadMoreComments}
            loadingMoreComments={loadingMoreComments}
            variant="compact"
            showDivider={false}
            showSupporterBadges={showSupporterBadges}
          />
        </div>
      )}
    </>
  );

  if (isClickable) {
    return (
      <Card
        className="p-6 h-full flex flex-col relative group"
        data-testid={`expense-card-${expense.id}`}
      >
        <RouteChangeLink
          href={`/groups/${expense.groupId}/expenses/${expense.id}`}
          className="absolute inset-0 z-0"
          aria-label={`View expense: ${expense.title}`}
          data-testid={`expense-card-link-${expense.id}`}
        >
          <span className="sr-only">View expense details</span>
        </RouteChangeLink>
        <div className="relative z-10 pointer-events-none">{cardContent}</div>
      </Card>
    );
  }

  return (
    <Card
      className="p-6 rounded-lg"
      data-testid={`expense-detail-card-${expense.id}`}
    >
      {cardContent}
    </Card>
  );
}

// Memoize ExpenseCard to prevent unnecessary re-renders
export const ExpenseCard = React.memo(
  ExpenseCardComponent,
  (prevProps, nextProps) => {
    // Re-render only if these key properties change
    return (
      prevProps.expense.id === nextProps.expense.id &&
      prevProps.expense.amount === nextProps.expense.amount &&
      prevProps.expense.title === nextProps.expense.title &&
      prevProps.expense.currency === nextProps.expense.currency &&
      prevProps.expense.date === nextProps.expense.date &&
      prevProps.expense.originalAmount === nextProps.expense.originalAmount &&
      prevProps.expense.originalCurrency ===
        nextProps.expense.originalCurrency &&
      prevProps.groupName === nextProps.groupName &&
      prevProps.variant === nextProps.variant &&
      prevProps.showNotes === nextProps.showNotes &&
      prevProps.showMultiplePayers === nextProps.showMultiplePayers &&
      prevProps.showPositionIndicator === nextProps.showPositionIndicator &&
      prevProps.showSocialInteractions === nextProps.showSocialInteractions &&
      prevProps.isClickable === nextProps.isClickable &&
      prevProps.loadingReaction === nextProps.loadingReaction &&
      prevProps.editHref === nextProps.editHref &&
      prevProps.showSupporterBadges === nextProps.showSupporterBadges &&
      JSON.stringify(prevProps.expense.aggregatedReactions) ===
        JSON.stringify(nextProps.expense.aggregatedReactions) &&
      JSON.stringify(prevProps.expense.comments) ===
        JSON.stringify(nextProps.expense.comments)
    );
  }
);

ExpenseCard.displayName = 'ExpenseCard';
